import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { House, Calculator } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import QuotaProgress from "@/components/QuotaProgress";
import UpgradeCTABanner from "@/components/UpgradeCTABanner";
import CommunityStats from "@/components/CommunityStats";
import CommunityCrossCharts from "@/components/CommunityCrossCharts";
import AnalyticsCharts from "@/components/AnalyticsCharts";
import OnlineUsersCard from "@/components/OnlineUsersCard";
import CollapseSection from "@/components/CollapseSection";
import MiniActivityChart from "@/components/MiniActivityChart";
import { useAuth } from "@/context/AuthContext";

// Landing page (route: /dashboard).
const CommunityDashboard = () => {
  const { user } = useAuth();
  const [greeting, setGreeting] = useState("");

  useEffect(() => {
    // Greeting bands (per user spec):
    //   05:00–16:59 → Buongiorno
    //   17:00–21:59 → Buonasera
    //   22:00–04:59 → Buonanotte  (wraps past midnight — "dalle 22 in poi")
    const h = new Date().getHours();
    let g;
    if (h >= 22 || h < 5) g = "Buonanotte";
    else if (h < 17)       g = "Buongiorno";
    else                    g = "Buonasera";
    setGreeting(g);
  }, []);

  const firstName = (user?.name || "").split(" ")[0] || "Ciao";
  const canPiano = !!user?.capabilities?.piano_economico || user?.role === "admin";

  const CtaCard = ({ to, testId, label, icon, tint }) => (
    <Link
      to={to}
      data-testid={testId}
      className="ios-card w-64 h-24 rounded-3xl bg-white/70 backdrop-blur-md hover:bg-white/90 border border-white/60 transition-all hover:-translate-y-0.5 flex items-center gap-4 px-5"
    >
      <img
        src={icon}
        alt=""
        aria-hidden="true"
        className="w-16 h-16 object-contain select-none shrink-0"
        style={{ filter: `drop-shadow(0 3px 8px ${tint})` }}
      />
      <span className="font-display font-bold text-lg text-slate-900 leading-tight">{label}</span>
    </Link>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />

      {/* Hero diorama */}
      <div className="w-full relative overflow-hidden bg-white" data-testid="dashboard-hero">
        <div
          className="w-full"
          style={{
            aspectRatio: "3.55 / 1",
            backgroundImage: "url('/diorama-immomap.png')",
            backgroundSize: "contain",
            backgroundPosition: "center top",
            backgroundRepeat: "no-repeat",
            backgroundColor: "#ffffff",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
          style={{ background: "linear-gradient(to bottom, transparent 0%, #f8fafc 100%)" }}
        />
      </div>

      <main
        className="max-w-7xl mx-auto px-4 md:px-6 pb-16 relative z-10"
        style={{ marginTop: "-25vw" }}
        data-testid="dashboard-main"
      >
        {/* Title row — greeting on the left, mini activity chart top-right. */}
        <div className="flex items-start justify-between gap-4">
          <div className="max-w-none">
            <div
              className="text-[11px] uppercase tracking-[0.3em] font-semibold"
              style={{ color: "#000000", textShadow: "0 0 3px rgba(255,255,255,1), 0 0 3px rgba(255,255,255,0.9)" }}
            >
              Dashboard
            </div>
            <h1
              className="font-display font-bold text-4xl md:text-5xl tracking-tighter leading-none mt-1"
              style={{ color: "#000000", textShadow: "0 0 3px rgba(255,255,255,1), 0 0 3px rgba(255,255,255,0.9)" }}
            >
              {greeting}, {firstName}.
            </h1>
            <p
              className="text-sm mt-2 max-w-2xl"
              style={{ color: "#000000", textShadow: "0 0 3px rgba(255,255,255,1), 0 0 3px rgba(255,255,255,0.9)" }}
            >
              Il tuo cruscotto ImmoMap. Consulta i pattern di mercato aggregati dalla community, monitora il tuo piano e vedi chi è al lavoro adesso.
            </p>
          </div>
          {/* Compact sparkline of the caller's activity (properties, boards,
              communities) — glow line only, no background, upper-right. */}
          <div className="shrink-0 hidden md:block">
            <MiniActivityChart />
          </div>
        </div>

        {/* Centered CTAs (rectangular). Schede + Piano Economico. */}
        <div
          className="flex flex-wrap items-center justify-center gap-4 mt-6"
          data-testid="dashboard-hero-cta-row"
          style={{ filter: "drop-shadow(0 0 3px rgba(255,255,255,1)) drop-shadow(0 0 6px rgba(255,255,255,0.75))" }}
        >
          <CtaCard
            to="/boards"
            testId="cta-open-boards-hero"
            label="Schede"
            icon="/board-icons/house.png"
            tint="rgba(255,149,0,0.35)"
          />
          {canPiano && (
            <CtaCard
              to="/piano-economico"
              testId="cta-open-piano-hero"
              label="Piano Economico"
              icon="/board-icons/calculator.png"
              tint="rgba(245,158,11,0.35)"
            />
          )}
        </div>

        {/* Plan usage + online users */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6" data-testid="dashboard-toprow">
          <div
            className="lg:col-span-2 [&_.ios-card]:bg-white/65 [&_.ios-card]:backdrop-blur-md"
            style={{ filter: "drop-shadow(0 0 3px rgba(255,255,255,1)) drop-shadow(0 0 6px rgba(255,255,255,0.75))" }}
          >
            <QuotaProgress refreshKey={0} className="h-full" />
          </div>
          <div
            className="[&_.ios-card]:bg-white/65 [&_.ios-card]:backdrop-blur-md"
            style={{ filter: "drop-shadow(0 0 3px rgba(255,255,255,1)) drop-shadow(0 0 6px rgba(255,255,255,0.75))" }}
          >
            <OnlineUsersCard />
          </div>
        </div>

        <UpgradeCTABanner className="mt-6" />

        {/* Statistiche del tuo portafoglio (was second → now first, per spec). */}
        <CollapseSection
          title="Statistiche del tuo portafoglio"
          subtitle="Il tuo archivio"
          testId="section-portfolio-stats"
          defaultOpen={true}
        >
          <AnalyticsCharts refreshKey={0} titleHidden />
        </CollapseSection>

        {/* Il mercato secondo ImmoMap (was first → now second). Community
            KPIs + cross-community aggregated charts. */}
        <CollapseSection
          title="Il mercato secondo ImmoMap"
          subtitle="Community · anonime"
          testId="section-market-stats"
          defaultOpen={true}
        >
          <CommunityStats titleHidden />
          <CommunityCrossCharts className="mt-4" />
        </CollapseSection>
      </main>
    </div>
  );
};

export default CommunityDashboard;
